import { createContext, useContext, type PropsWithChildren } from 'react';
import type { HevitonApiAppLegacy } from '@heviton/heviton-api-app-legacy';
import type { HevitonApiV2 } from '@heviton/heviton-api-v2';
import type { HevitonApiWebLegacy } from '@heviton/heviton-api-web-legacy';

const ApiV3Context = createContext<HevitonApiAppLegacy | null>(null);

export interface ApiAppLegacyProviderProps extends PropsWithChildren {
  /** 미리 만들어진 `HevitonApiAppLegacy` 인스턴스. lifecycle owner 는 호출자 (app/providers). */
  api: HevitonApiAppLegacy;
}

/**
 * `HevitonApiAppLegacy` context provider — 인스턴스 lifecycle 은 모름. 인스턴스 생성·환경
 * 의존(VITE_API_BASE_URL)·콜백 연결은 모두 호출자(app layer)가 책임 (inversion
 * of control). shared 가 도메인/환경 무지 유지.
 */
export function ApiAppLegacyProvider({ children, api }: ApiAppLegacyProviderProps) {
  return <ApiV3Context.Provider value={api}>{children}</ApiV3Context.Provider>;
}

export function useApiAppLegacy(): HevitonApiAppLegacy {
  const ctx = useContext(ApiV3Context);
  if (!ctx) throw new Error('useApiAppLegacy must be used within ApiAppLegacyProvider');
  return ctx;
}

// --- v2 (병행 데이터 도메인 /api/v2) — v3 와 동형의 별도 context ---

const ApiV2Context = createContext<HevitonApiV2 | null>(null);

export interface ApiV2ProviderProps extends PropsWithChildren {
  /** 미리 만들어진 `HevitonApiV2` 인스턴스. lifecycle owner 는 호출자 (app/providers). */
  api: HevitonApiV2;
}

export function ApiV2Provider({ children, api }: ApiV2ProviderProps) {
  return <ApiV2Context.Provider value={api}>{children}</ApiV2Context.Provider>;
}

export function useApiV2(): HevitonApiV2 {
  const ctx = useContext(ApiV2Context);
  if (!ctx) throw new Error('useApiV2 must be used within ApiV2Provider');
  return ctx;
}

// --- web-legacy (레거시 데이터 도메인 /api/2.0) — v3/v2 와 동형의 별도 context ---

const ApiWebLegacyContext = createContext<HevitonApiWebLegacy | null>(null);

export interface ApiWebLegacyProviderProps extends PropsWithChildren {
  /** 미리 만들어진 `HevitonApiWebLegacy` 인스턴스. lifecycle owner 는 호출자 (app/providers). */
  api: HevitonApiWebLegacy;
}

export function ApiWebLegacyProvider({ children, api }: ApiWebLegacyProviderProps) {
  return <ApiWebLegacyContext.Provider value={api}>{children}</ApiWebLegacyContext.Provider>;
}

export function useApiWebLegacy(): HevitonApiWebLegacy {
  const ctx = useContext(ApiWebLegacyContext);
  if (!ctx) throw new Error('useApiWebLegacy must be used within ApiWebLegacyProvider');
  return ctx;
}
